import React, { useState } from "react";

import {
  Box,
  Button,
  Container,
  Typography,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import { useGo, useList } from "@refinedev/core";
import PlaygroundBreadcrumbs from "../../components/common/playground-breadcrumbs";
import { Brand } from "../playground/types";
import { BrandCreate } from "./create";
import { BrandEdit } from "./edit";
import { useAuthStore } from "../../authProvider";
import { exportTimeTracking } from "../../api/batches";
import { downloadHelper } from "../../utils/download";

export const BrandList: React.FC = () => {
  const { user } = useAuthStore();

  const [showArchived, setShowArchived] = useState(false);
  const [brandModalOpen, setBrandModalOpen] = useState<
    string | "closed" | "create"
  >("closed");

  const { data, isError, error, isLoading } = useList({
    pagination: {
      current: 1,
    },
    resource: "brand",
    meta: {
      params: {
        archived: showArchived,
      },
    },
  });

  const extra: {
    active_total: number;
    archived_total: number;
  } = data?.extra as {
    active_total: number;
    archived_total: number;
  };

  const go = useGo();

  const brands: Brand[] = data?.data as Brand[];

  return (
    <Container
      maxWidth="lg"
      sx={{
        flex: 1,
        display: "flex",
        p: 4,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <PlaygroundBreadcrumbs />

        {isLoading && <div>Loading...</div>}

        {isError && <div>Error: {error.message}</div>}

        {brandModalOpen === "create" && (
          <BrandCreate
            onClose={() => {
              setBrandModalOpen("closed");
            }}
          />
        )}

        {brandModalOpen !== "closed" && brandModalOpen !== "create" && (
          <BrandEdit
            brandId={brandModalOpen}
            brandData={
              brands?.find((brand) => brand.id === brandModalOpen) as Brand
            }
            onClose={() => {
              setBrandModalOpen("closed");
            }}
          />
        )}

        {brands && (
          <>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h4" component="h1" sx={{ mb: 0 }}>
                  Brands
                </Typography>
                {!user?.isClientRelated && (
                  <Tabs
                    value={showArchived ? 1 : 0}
                    onChange={(_, newValue) =>
                      setShowArchived(Boolean(newValue))
                    }
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <span>Active</span>
                          {(extra?.active_total || 0) > 0 && (
                            <Badge
                              badgeContent={extra?.active_total}
                              color="warning"
                            />
                          )}
                        </Box>
                      }
                    />
                    <Tab
                      iconPosition="start"
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <span>Archived</span>
                          {(extra?.archived_total || 0) > 0 && (
                            <Badge
                              badgeContent={extra?.archived_total}
                              color="error"
                            />
                          )}
                        </Box>
                      }
                    />
                  </Tabs>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {user?.isAdmin && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={async () => {
                      await downloadHelper(() => exportTimeTracking());
                    }}
                  >
                    Export time tracking
                  </Button>
                )}
                {(user?.isAdmin || user?.isManager) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setBrandModalOpen("create")}
                  >
                    Create New Brand
                  </Button>
                )}
              </Box>
            </Box>
            <>
              {brands.map((brand: Brand) => (
                <Box
                  key={brand.id}
                  sx={{
                    width: "100%",
                    p: 2,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    go({
                      to: {
                        resource: "batch",
                        action: "list",
                        meta: {
                          brandName: brand.name,
                          brandId: brand.id,
                        },
                      },
                    });
                  }}
                >
                  <Typography variant="h6">{brand.name}</Typography>
                  {(user?.isAdmin || user?.isManager) && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();

                        if (brand.id) {
                          setBrandModalOpen(brand.id);
                        }
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
              ))}
            </>
          </>
        )}
      </Box>
    </Container>
  );
};
